<template>
	<form @submit.prevent="handleSubmit" class="pairing-component">
		<div class="code-section">
			<label class="section-label">Enter Pairing Code</label>
			
<!--&lt;!&ndash;			Optional plain text input if otp-display below causes issues&ndash;&gt;-->
<!--			<div class="code-input-group">-->
<!--				<input-->
<!--						ref="codeInputEl"-->
<!--						v-model="pairCode"-->
<!--						class="code-input mono"-->
<!--						maxlength="6"-->
<!--						type="text"-->
<!--						inputmode="numeric"-->
<!--						pattern="[0-9]*"-->
<!--						autocomplete="one-time-code"-->
<!--						placeholder="&#45;&#45;&#45;&#45;&#45;&#45;"-->
<!--						:disabled="isValidated || isLoading"-->
<!--						:class="{ 'input-error': hasError }"-->
<!--				/>-->
<!--			</div>-->
			
			<div class="otp-wrapper" @click="handleWrapperClick">
				<div class="otp-display">
					<div
							v-for="i in 6"
							:key="i"
							class="otp-box mono"
							:class="{
							'filled': pairCode[i-1],
							'active': isFocused && pairCode.length === i-1 && !isValidated && !isLoading,
							'success': isValidated,
							'error': hasError
						}"
					>
						{{ pairCode[i - 1] || '' }}
					</div>
				</div>
				
				<input
						ref="codeInputEl"
						v-model="pairCode"
						type="text"
						class="hidden-otp-input"
						maxlength="6"
						inputmode="numeric"
						pattern="[0-9]*"
						autocomplete="one-time-code"
						:disabled="isValidated || isLoading"
						@focus="isFocused = true"
						@blur="isFocused = false"
				/>
			</div>
			
			<div class="feedback-container" :class="feedbackType">
				<span v-if="isLoading && !isValidated" class="spinner small-spinner"></span>
				<p v-if="feedbackMessage || (isLoading && !isValidated)" class="feedback-text">
					{{ isLoading && !isValidated ? 'Validating...' : feedbackMessage }}
				</p>
			</div>
		</div>
		
		<div class="instructions-dropdown" v-if="!isValidated">
			<button
					type="button"
					class="instructions-button"
					@click="showInstructions = !showInstructions"
			>
				Pairing instructions
				<Icon
						class="instructions-arrow"
						:class="{ 'is-open': showInstructions }"
						name="arrow-down"
						size="sm"
				></Icon>
			</button>
			
			<Transition name="expand">
				<div v-if="showInstructions" class="dropdown-content-wrapper">
					<div class="dropdown-content">
						<ol class="instructions-list">
							<li>Launch a web browser on your Display</li>
							<li>Head to <span>tv.buzztime.com</span></li>
							<li>If this display has never been paired, it will show a 6-digit pairing code</li>
							<li>Locate this pairing code and enter it above</li>
						</ol>
					</div>
				</div>
			</Transition>
		</div>
		
<!--	Uncomment when we want to add editable name/ default app. Will use /pairing/claim endpoint	-->
<!--		<Transition name="expand">-->
<!--			<div v-if="isValidated" class="details-section">-->
<!--				<Divider class="form-divider"/>-->
<!--				-->
<!--				<div class="field">-->
<!--					<label for="tvName">Name this Display</label>-->
<!--					<input-->
<!--							id="tvName"-->
<!--							ref="tvNameInputEl"-->
<!--							v-model.trim="tvName"-->
<!--							type="text"-->
<!--							required-->
<!--							placeholder="Bar TV 1"-->
<!--							:disabled="isLoading"-->
<!--					/>-->
<!--				</div>-->
<!--				-->
<!--				<div class="field">-->
<!--					<label for="defaultApp">Default Launch App</label>-->
<!--&lt;!&ndash;					<select id="defaultApp" v-model="defaultApp" :disabled="isLoading">&ndash;&gt;-->
<!--					<select id="defaultApp" v-model="defaultApp" disabled>-->
<!--						<option v-for="app in defaultApps" :key="app.value" :value="app.value">-->
<!--							{{ app.label }}-->
<!--						</option>-->
<!--					</select>-->
<!--				</div>-->
<!--			</div>-->
<!--		</Transition>-->
	</form>
</template>

<script setup>
import {ref, computed, watch, nextTick, onMounted} from 'vue'
import {useMainStore} from '../store/main'
import Divider from './Divider.vue'
import Icon from './Icon.vue' // Added missing import for instructions

const store = useMainStore()
const emit = defineEmits(['success', 'update:state'])

// State
const pairCode = ref('')
const errorMsg = ref('')
const hasError = ref(false)
const isLoading = ref(false)
const isValidated = ref(false)
const codeInputEl = ref(null)
const tvNameInputEl = ref(null)
const showInstructions = ref(false)
const isFocused = ref(false)

// Form data
const tvName = ref('')
const defaultApp = ref('trivia')

const feedbackMessage = computed(() => {
	if (errorMsg.value) return errorMsg.value
	if (isValidated.value) return 'Code verified'
	return ''
})

const feedbackType = computed(() => {
	if (errorMsg.value) return 'error-state'
	if (isValidated.value) return 'success-state'
	return 'info-state'
})

const canSubmitDetails = computed(() => {
	return isValidated.value && tvName.value.trim().length > 0
})


// TODO: replace with fetched store.apps
const defaultApps = [
	{value: 'none', label: 'None'},
	{value: 'trivia', label: 'Network Trivia'},
]

watch([isValidated, isLoading, canSubmitDetails], () => {
	emit('update:state', {
		isValidated: isValidated.value,
		isLoading: isLoading.value,
		canSubmit: canSubmitDetails.value
	})
}, {immediate: true})

watch(pairCode, (newVal) => {
	const sanitized = newVal.replace(/\D/g, '')
	if (sanitized !== newVal) {
		pairCode.value = sanitized
		return
	}
	
	if (hasError.value) {
		clearError()
	}
	
	if (sanitized.length === 6 && !isLoading.value && !isValidated.value) {
		validateCode()
	}
})


function getAuthToken() {
	return sessionStorage.getItem('buzztime_token') ||
			sessionStorage.getItem('auth_token') ||
			localStorage.getItem('buzztime_token')
}

function isLocalDev() {
	return process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost'
}

function handleWrapperClick() {
	if (!isValidated.value && !isLoading.value && codeInputEl.value) {
		codeInputEl.value.focus()
		// If they click back into a failed code, highlight it all so the next keystroke overwrites it
		if (pairCode.value.length === 6) {
			codeInputEl.value.select()
		}
	}
}

function focusElement(elRef, selectText = false) {
	// A small timeout ensures DOM updates finish
	setTimeout(() => {
		if (elRef.value) {
			elRef.value.focus()
			if (selectText) {
				elRef.value.select()
			}
		}
	}, 300) // 300ms allows the expand transition to complete before focusing
}

function clearError() {
	errorMsg.value = ''
	hasError.value = false
}

function setError(message) {
	errorMsg.value = message
	hasError.value = true
	isValidated.value = false
	
	focusElement(codeInputEl)
}

// Master submit handler routing to correct step
function handleSubmit() {
	if (!isValidated.value) {
		if (pairCode.value.length === 6) validateCode()
	} else {
		saveDetails()
	}
}

async function validateCode() {
	isLoading.value = true
	clearError()
	
	// // ==========================================
	// // DEV MOCK: Delete or comment out this block to restore real backend
	// // ==========================================
	// setTimeout(() => {
	// 	isValidated.value = true
	// 	isLoading.value = false
	// 	// focusElement(tvNameInputEl)
	// 	store.refreshSiteData();
	// 	// store.addTv({
	// 	// 	id: result.tv.id,
	// 	// 	name: result.tv.name,
	// 	// 	code: pairCode.value,
	// 	// 	status: 'active',
	// 	// 	defaultApp: defaultApp.value
	// 	// })
	// 	emit('success')
	//
	// }, 3000)
	//
	// return
	// // ==========================================
	// // END DEV MOCK
	
	try {
		const token = getAuthToken()
		if (!token && !isLocalDev()) {
			throw new Error('Authentication required.')
		}
		
		const headers = {'Content-Type': 'application/json'}
		if (token) headers['Authorization'] = `Bearer ${token}`
		
		const url = import.meta.env.VITE_CHEERIOS_V2_API_BASE_URL
		const response = await fetch(`${url}/remote/pairing/validate`, {
			method: 'POST',
			headers,
			body: JSON.stringify({code: pairCode.value}),
			// signal: AbortSignal.timeout(15000)
		})
		
		if (!response.ok) {
			let message = "An unexpected error occurred. Please try again";
			
			try {
				const errorData = await response.json();
				message = errorData.detail || errorData.error || message
			} catch {}
			
			setError(message);
			return;
		}
		
		const data = await response.json()
		
		if (response.status === 200) {
			isValidated.value = true
			emit('success')
			await store.refreshSiteData();
			
			// Uncomment when we want to add editable names
			// tvName.value = data.tv?.suggested_name || `TV ${pairCode.value}`
			// focusElement(tvNameInputEl)
		} else {
			setError(data.message || data.detail || 'Validation failed. Please try again.')
		}
		
	} catch (error) {
		setError(error)
		// handleValidationError(error)
	} finally {
		isLoading.value = false
		if (!isValidated.value) {
			focusElement(codeInputEl, true)
		}
	}
}

async function handleApiError(response) {
	let message = 'An unexpected error occurred.'
	try {
		const errorData = await response.json()
		message = errorData.message || errorData.error || message
	} catch {
	}
	
	switch (response.status) {
		case 400:
			message = 'Invalid code format.';
			break;
		case 401:
			message = 'Authentication failed.';
			break;
		case 404:
			message = 'Code not found or expired.';
			break;
		case 409:
			message = 'This TV is already paired.';
			break;
		case 429:
			message = 'Too many attempts. Wait a moment.';
			break;
		case 500:
			message = 'Server error. Try again shortly.';
			break;
	}
	setError(message)
}

function handleValidationError(error) {
	let message = 'Unable to validate code. Check your connection.'
	if (error.name === 'AbortError' || error.message.includes('timeout')) {
		message = 'Request timed out.'
	} else if (error.message.includes('Authentication')) {
		message = error.message
	}
	setError(message)
}

async function saveDetails() {
	if (!tvName.value.trim()) {
		errorMsg.value = 'Please enter a display name.'
		return
	}
	
	isLoading.value = true
	clearError()
	
	try {
		const token = getAuthToken()
		if (!token && !isLocalDev()) {
			throw new Error('Authentication required.')
		}
		
		const headers = {'Content-Type': 'application/json'}
		if (token) headers['Authorization'] = `Bearer ${token}`
		
		const response = await fetch(`${__API_BASE_URL__}/buzztime-tv/pairing/claim`, {
			method: 'POST',
			headers,
			body: JSON.stringify({
				code: pairCode.value,
				tv_name: tvName.value.trim(),
				default_app: defaultApp.value
			}),
			signal: AbortSignal.timeout(15000)
		})
		
		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}))
			throw new Error(errorData.message || `Server error (${response.status})`)
		}
		
		const result = await response.json()
		
		if (result.success && result.tv) {
			store.addTv({
				id: result.tv.id,
				name: result.tv.name,
				code: pairCode.value,
				status: 'active',
				defaultApp: defaultApp.value
			})
			emit('success')
		} else {
			throw new Error(result.message || 'Failed to add TV')
		}
		
	} catch (error) {
		setError(error.message || 'Failed to add TV. Please try again.')
	} finally {
		isLoading.value = false
	}
}

defineExpose({
	saveDetails
})

onMounted(() => {
	const pendingCode = sessionStorage.getItem('pending_pairing_code')
	
	if (pendingCode) {
		pairCode.value = pendingCode
		sessionStorage.removeItem('pending_pairing_code')
	} else {
		// Normal manual pairing flow
		focusElement(codeInputEl)
	}
})
</script>

<style scoped>
.pairing-component {
	display: flex;
	flex-direction: column;
	width: 100%;
}

.instructions-dropdown {
	margin-bottom: var(--spacing-lg);
	width: 100%;
}

.instructions-button {
	background: var(--color-popup-light-2);
	border: none;
	color: var(--color-text);
	cursor: pointer;
	font-size: var(--font-size-sm);
	font-weight: var(--font-weight-semibold);
	text-transform: uppercase;
	letter-spacing: 0.05em;
	user-select: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: opacity 0.2s ease;
	width: 100%;
	padding: 0;
}

.instructions-button:hover {
	opacity: 0.8;
}

.instructions-arrow {
	transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.instructions-arrow.is-open {
	transform: rotate(180deg);
}

.dropdown-content {
	/*border: var(--border-thin) var(--color-border);*/
	/*border-radius: var(--border-radius-sm);*/
	padding: var(--spacing-md) var(--spacing-xl);
	margin-top: var(--spacing-sm);
	text-align: left;
}

.instructions-list {
	margin: 0;
	padding-left: var(--spacing-sm);
	color: var(--color-text-secondary);
	font-size: var(--font-size-base);
	line-height: 1.6;
}

.instructions-list li::marker {
	color: var(--color-primary);
	font-weight: bold;
}

.instructions-list span {
	font-weight: var(--font-weight-bold);
	color: var(--color-text);
}

/* --- Typography & Labels --- */
.section-label {
	display: block;
	margin-bottom: var(--spacing-sm);
	font-weight: var(--font-weight-semibold);
	font-size: var(--font-size-sm);
	color: var(--color-text-secondary);
	text-transform: uppercase;
	letter-spacing: var(--spacing-xs);
	text-align: center;
}

/*!* --- Code Input --- *!*/
/*.code-input-group {*/
/*	margin: var(--spacing-md) 0;*/
/*}*/

/*.code-input {*/
/*	width: 80%;*/
/*	text-align: center;*/
/*	font-size: 3.5rem;*/
/*	letter-spacing: 0.2em;*/
/*	padding: var(--spacing-md) 0;*/
/*	background: transparent;*/
/*	border: none;*/
/*	border-bottom: var(--border-thick) var(--color-border);*/
/*	color: var(--color-text);*/
/*	transition: border-color 0.2s ease;*/
/*}*/

/*.code-input:focus {*/
/*	outline: none;*/
/*	border-bottom-color: var(--color-primary);*/
/*}*/

/*.code-input::placeholder {*/
/*	color: var(--color-text-secondary);*/
/*	opacity: 0.2;*/
/*}*/

/*.code-input.input-error {*/
/*	border-bottom-color: var(--color-error) !important;*/
/*}*/

/* --- OTP Input --- */
.otp-wrapper {
	position: relative;
	width: 100%;
	margin-bottom: var(--spacing-md);
	display: flex;
	justify-content: center;
}

.otp-display {
	display: flex;
	gap: var(--spacing-sm);
	justify-content: center;
	width: 100%;
	pointer-events: none;
}

.otp-box {
	width: 3rem;
	height: 4rem;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: var(--font-size-2xl);
	font-weight: var(--font-weight-bold);
	border: var(--border-thick) var(--color-border);
	border-radius: var(--border-radius-sm);
	background: var(--color-surface-2);
	color: var(--color-text);
	transition: all var(--transition-fast);
}

.otp-box.active {
	border-color: var(--color-primary);
	box-shadow: 0 0 0 2px var(--color-primary-2);
}

.otp-box.filled {
	border-color: var(--color-text-secondary);
}

.otp-box.success {
	border-color: var(--color-success);
	background: var(--color-success-3);
	color: var(--color-success);
}

.otp-box.error {
	border-color: var(--color-error);
	background: var(--color-error-3);
	color: var(--color-error);
}

.hidden-otp-input {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	opacity: 0;
	cursor: text;
	font-size: 16px;
}

/* --- Feedback --- */
.feedback-container {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--spacing-sm);
	height: 1.5rem;
	margin-bottom: var(--spacing-md);
}

.feedback-text {
	margin: 0;
	font-size: var(--font-size-sm);
	font-weight: var(--font-weight-medium);
}

.error-state .feedback-text {
	color: var(--color-error);
}

.success-state .feedback-text {
	color: var(--color-success);
}

.info-state .feedback-text {
	color: var(--color-text-secondary);
}

/* --- Progressive Disclosure Form --- */
.details-section {
	display: flex;
	flex-direction: column;
	width: 100%;
}

.form-divider {
	margin: 0 0 var(--spacing-md) 0;
}

.field {
	margin-bottom: var(--spacing-md);
}

label {
	display: block;
	margin-bottom: var(--spacing-xs);
	font-weight: var(--font-weight-semibold);
	font-size: var(--font-size-sm);
	color: var(--color-text-secondary);
}

.details-section input[type="text"], select {
	width: 100%;
	padding: var(--spacing-md);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-sm);
	background: var(--color-surface-2);
	color: var(--color-text);
	font-size: var(--font-size-base);
}

.details-section input:focus, select:focus {
	outline: none;
	border-color: var(--color-primary);
}

select:disabled {
	opacity: 0.6;
}

/* --- Actions --- */
.actions {
	display: flex;
	justify-content: center;
	width: 100%;
	gap: var(--spacing-md);
	margin-top: var(--spacing-md);
}

.split-actions {
	justify-content: space-between;
}

button {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--spacing-sm);
	padding: var(--spacing-md) var(--spacing-lg);
	border-radius: var(--border-radius-sm);
	font-weight: var(--font-weight-bold);
	font-size: var(--font-size-base);
	cursor: pointer;
	transition: all var(--transition-fast);
	border: none;
}

.flex-grow {
	flex: 1;
}

.primary {
	background: var(--color-primary);
	color: var(--color-text);
}

.primary:hover:not(:disabled) {
	filter: brightness(1.1);
}

.primary:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.ghost {
	background: transparent;
	color: var(--color-text);
	border: var(--border-thin) var(--color-border);
}

.ghost:hover:not(:disabled) {
	background: var(--color-surface-2);
}

/* --- Spinners --- */
.spinner {
	width: 1.25rem;
	height: 1.25rem;
	border: 2px solid transparent;
	border-top: 2px solid currentColor;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

.small-spinner {
	width: 1rem;
	height: 1rem;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* --- Expand Transition --- */
.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	overflow: hidden;
	max-height: 400px;
	opacity: 1;
}

.expand-enter-from,
.expand-leave-to {
	max-height: 0;
	opacity: 0;
	margin-top: 0;
}
</style>